import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../client';
import { queryKeys } from '../query-keys';
import type {
  Item,
  ItemWithRelations,
  ItemWithInstanceCounts,
  ItemInstance,
  DashboardStats,
  ItemQueryParams,
  CreateItemData,
  UpdateItemData,
  CreateItemInstanceData,
  UpdateItemInstanceData,
  UpdateInstanceDetailsData,
  BulkUpdateInstancesData,
  BulkUpdateResult,
  BulkCreateItemsData,
  BulkCreateResult,
  Product,
  SetItemStockTargetDto,
  ItemCreateResult,
  AutoGroupSweepResult,
} from '../types';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Normalize a barcode for comparison by removing leading zeros.
 * This handles inconsistencies where the same barcode might be stored
 * with different padding (e.g., "057000243760" vs "0057000243760").
 *
 * @param barcode - The barcode string to normalize
 * @returns Normalized barcode with leading zeros removed
 */
function normalizeBarcode(barcode: string): string {
  // Remove leading zeros but keep at least one character
  return barcode.replace(/^0+/, '') || '0';
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Fetch all items for a household.
 * Returns items with joined product and storage location data,
 * plus computed activeQuantity and activeInstanceCount.
 */
async function getItems(
  householdId: string,
  params?: ItemQueryParams
): Promise<ItemWithInstanceCounts[]> {
  const searchParams = new URLSearchParams();
  if (params?.storageLocationId) searchParams.set('storageLocationId', params.storageLocationId);
  if (params?.productId) searchParams.set('productId', params.productId);
  if (params?.search) searchParams.set('search', params.search);
  if (params?.limit) searchParams.set('limit', params.limit.toString());
  if (params?.offset) searchParams.set('offset', params.offset.toString());
  if (params?.hideDepleted !== undefined)
    searchParams.set('hideDepleted', String(params.hideDepleted));

  const query = searchParams.toString();
  const url = `/households/${householdId}/items${query ? `?${query}` : ''}`;
  return apiFetch<ItemWithInstanceCounts[]>(url);
}

/**
 * Fetch a single item with product relation.
 */
async function getItem(householdId: string, id: string): Promise<ItemWithRelations> {
  return apiFetch<ItemWithRelations>(`/households/${householdId}/items/${id}`);
}

/**
 * Create a new item.
 */
async function createItem(householdId: string, data: CreateItemData): Promise<ItemCreateResult> {
  return apiFetch<ItemCreateResult>(`/households/${householdId}/items`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update an existing item.
 */
async function updateItem(householdId: string, id: string, data: UpdateItemData): Promise<Item> {
  return apiFetch<Item>(`/households/${householdId}/items/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/**
 * Delete an item.
 */
async function deleteItem(householdId: string, id: string): Promise<void> {
  await apiFetch(`/households/${householdId}/items/${id}`, {
    method: 'DELETE',
  });
}

/**
 * Fetch dashboard statistics.
 */
async function getDashboardStats(householdId: string): Promise<DashboardStats> {
  return apiFetch<DashboardStats>(`/households/${householdId}/items/stats`);
}

/**
 * Fetch items expiring soon.
 * Returns items with joined product and storage location data,
 * plus computed activeQuantity and activeInstanceCount.
 *
 * @param householdId - The household ID
 * @param days - Optional number of days to look ahead (uses household default if omitted)
 */
async function getExpiringSoon(
  householdId: string,
  days?: number
): Promise<ItemWithInstanceCounts[]> {
  const params = days !== undefined ? `?days=${days}` : '';
  return apiFetch<ItemWithInstanceCounts[]>(
    `/households/${householdId}/items/expiring-soon${params}`
  );
}

/**
 * Fetch instances for an item.
 */
export async function getItemInstances(
  householdId: string,
  itemId: string
): Promise<ItemInstance[]> {
  return apiFetch<ItemInstance[]>(`/households/${householdId}/items/${itemId}/instances`);
}

/**
 * Create a new item instance.
 */
async function createItemInstance(
  householdId: string,
  itemId: string,
  data: CreateItemInstanceData
): Promise<ItemInstance> {
  return apiFetch<ItemInstance>(`/households/${householdId}/items/${itemId}/instances`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update an item instance status.
 */
async function updateItemInstance(
  householdId: string,
  itemId: string,
  instanceId: string,
  data: UpdateItemInstanceData
): Promise<ItemInstance> {
  return apiFetch<ItemInstance>(
    `/households/${householdId}/items/${itemId}/instances/${instanceId}`,
    {
      method: 'PATCH',
      body: JSON.stringify(data),
    }
  );
}

/**
 * Update instance details (quantity, dates).
 * Use for corrections, not status changes.
 */
async function updateInstanceDetails(
  householdId: string,
  itemId: string,
  instanceId: string,
  data: UpdateInstanceDetailsData
): Promise<ItemInstance> {
  return apiFetch<ItemInstance>(
    `/households/${householdId}/items/${itemId}/instances/${instanceId}/details`,
    {
      method: 'PATCH',
      body: JSON.stringify(data),
    }
  );
}

/**
 * Bulk update multiple item instances.
 * Used for meal prep mode where multiple items are consumed/wasted at once.
 */
async function bulkUpdateInstances(
  householdId: string,
  data: BulkUpdateInstancesData
): Promise<BulkUpdateResult> {
  return apiFetch<BulkUpdateResult>(`/households/${householdId}/items/bulk-update`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Bulk create multiple items with initial instances.
 * Used for batch item entry where multiple items are added at once.
 */
async function bulkCreateItems(
  householdId: string,
  data: BulkCreateItemsData
): Promise<BulkCreateResult> {
  return apiFetch<BulkCreateResult>(`/households/${householdId}/items/bulk-create`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook to fetch all items for a household.
 * Supports filtering by storage location, product, and search term.
 */
export function useItems(householdId: string, params?: ItemQueryParams) {
  return useQuery({
    queryKey: queryKeys.items.list(householdId, params as Record<string, unknown>),
    queryFn: () => getItems(householdId, params),
    enabled: !!householdId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to fetch a single item.
 */
export function useItem(householdId: string, id: string) {
  return useQuery({
    queryKey: queryKeys.items.detail(householdId, id),
    queryFn: () => getItem(householdId, id),
    enabled: !!householdId && !!id,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to fetch dashboard statistics.
 * Uses shorter stale time since stats change frequently.
 */
export function useDashboardStats(householdId: string) {
  return useQuery({
    queryKey: queryKeys.items.stats(householdId),
    queryFn: () => getDashboardStats(householdId),
    enabled: !!householdId,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch items expiring soon.
 * @param householdId - The household ID
 * @param options - Optional query options (enabled, days)
 * @param options.enabled - Whether to enable the query (default: true)
 * @param options.days - Number of days to look ahead (uses household default if omitted)
 */
export function useExpiringSoon(
  householdId: string,
  options?: { enabled?: boolean; days?: number }
) {
  const isEnabled = options?.enabled !== undefined ? options.enabled : true;
  return useQuery({
    queryKey: queryKeys.items.expiringSoon(householdId, options?.days),
    queryFn: () => getExpiringSoon(householdId, options?.days),
    enabled: !!householdId && isEnabled,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch instances for an item.
 */
export function useItemInstances(householdId: string, itemId: string) {
  return useQuery({
    queryKey: queryKeys.items.instances(householdId, itemId),
    queryFn: () => getItemInstances(householdId, itemId),
    enabled: !!householdId && !!itemId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Hook to create a new item.
 * Invalidates all item queries for the household on success.
 * Also invalidates product groups when the item was auto-grouped.
 */
export function useCreateItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ householdId, data }: { householdId: string; data: CreateItemData }) =>
      createItem(householdId, data),
    onSuccess: (result, { householdId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.items.byHousehold(householdId),
      });
      if (result.autoGrouped) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.productGroups.byHousehold(householdId),
        });
      }
    },
  });
}

/**
 * Hook to update an item.
 * Updates the detail cache and invalidates the list.
 */
export function useUpdateItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      householdId,
      id,
      data,
    }: {
      householdId: string;
      id: string;
      data: UpdateItemData;
    }) => updateItem(householdId, id, data),
    onSuccess: (updatedItem, { householdId, id }) => {
      queryClient.setQueryData(queryKeys.items.detail(householdId, id), updatedItem);
      queryClient.invalidateQueries({
        queryKey: queryKeys.items.byHousehold(householdId),
      });
    },
  });
}

/**
 * Hook to delete an item.
 * Invalidates all item queries for the household on success.
 */
export function useDeleteItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ householdId, id }: { householdId: string; id: string }) =>
      deleteItem(householdId, id),
    onSuccess: (_, { householdId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.items.byHousehold(householdId),
      });
    },
  });
}

/**
 * Hook to create a new item instance.
 * Invalidates item instances and stats on success.
 */
export function useCreateItemInstance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      householdId,
      itemId,
      data,
    }: {
      householdId: string;
      itemId: string;
      data: CreateItemInstanceData;
    }) => createItemInstance(householdId, itemId, data),
    onSuccess: (_, { householdId, itemId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.items.instances(householdId, itemId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.items.stats(householdId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.items.expiringSoonAll(householdId),
      });
    },
  });
}

/**
 * Hook to update an item instance status.
 * Invalidates item instances, stats, and item list on success
 * (since activeQuantity may have changed).
 */
export function useUpdateItemInstance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      householdId,
      itemId,
      instanceId,
      data,
    }: {
      householdId: string;
      itemId: string;
      instanceId: string;
      data: UpdateItemInstanceData;
    }) => updateItemInstance(householdId, itemId, instanceId, data),
    onSuccess: (_, { householdId, itemId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.items.instances(householdId, itemId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.items.stats(householdId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.items.expiringSoonAll(householdId),
      });
      // Also invalidate item list since activeQuantity changes
      queryClient.invalidateQueries({
        queryKey: queryKeys.items.byHousehold(householdId),
      });
    },
  });
}

/**
 * Hook to update instance details (quantity, dates).
 * Use for corrections, not status changes.
 * Invalidates item instances and item list on success.
 */
export function useUpdateInstanceDetails() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      householdId,
      itemId,
      instanceId,
      data,
    }: {
      householdId: string;
      itemId: string;
      instanceId: string;
      data: UpdateInstanceDetailsData;
    }) => updateInstanceDetails(householdId, itemId, instanceId, data),
    onSuccess: (_, { householdId, itemId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.items.instances(householdId, itemId),
      });
      // Invalidate item list since activeQuantity may have changed
      queryClient.invalidateQueries({
        queryKey: queryKeys.items.byHousehold(householdId),
      });
    },
  });
}

/**
 * Hook to bulk update multiple item instances.
 * Used for meal prep mode where multiple items are consumed/wasted at once.
 * Invalidates all item-related queries for the household on success.
 */
export function useBulkUpdateInstances() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ householdId, data }: { householdId: string; data: BulkUpdateInstancesData }) =>
      bulkUpdateInstances(householdId, data),
    onSuccess: (_, { householdId }) => {
      // Invalidate all item-related queries since multiple items were updated
      // The byHousehold key is a prefix that matches all item queries including instances
      queryClient.invalidateQueries({
        queryKey: queryKeys.items.byHousehold(householdId),
      });
      // Also invalidate dashboard stats and expiring soon
      queryClient.invalidateQueries({
        queryKey: queryKeys.items.stats(householdId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.items.expiringSoonAll(householdId),
      });
    },
  });
}

/**
 * Hook to bulk create multiple items with instances.
 * Used for batch item entry where multiple items are added at once.
 * Invalidates all item-related queries for the household on success.
 */
export function useBulkCreateItems() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ householdId, data }: { householdId: string; data: BulkCreateItemsData }) =>
      bulkCreateItems(householdId, data),
    onSuccess: (_, { householdId }) => {
      // Invalidate all item-related queries since multiple items were created
      queryClient.invalidateQueries({
        queryKey: queryKeys.items.byHousehold(householdId),
      });
      // Also invalidate dashboard stats and expiring soon
      queryClient.invalidateQueries({
        queryKey: queryKeys.items.stats(householdId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.items.expiringSoonAll(householdId),
      });
    },
  });
}

// ============================================================================
// Barcode-to-Inventory Matching
// ============================================================================

/**
 * Result type for barcode-to-inventory lookup.
 */
export interface InventoryBarcodeResult {
  /** The product found by barcode (null if not found) */
  product: Product | null;
  /** Active instances sorted by expiry date (FIFO - earliest first, nulls last) */
  instances: ItemInstance[];
  /** Items that have this product */
  items: ItemWithInstanceCounts[];
}

/**
 * Fetch a product by barcode.
 */
async function getProductByBarcode(barcode: string): Promise<Product | null> {
  try {
    return await apiFetch<Product>(`/products/barcode/${barcode}`);
  } catch {
    return null;
  }
}

/**
 * Hook to find inventory items by barcode.
 * This performs a multi-step lookup with fallback:
 * 1. Look up product by barcode
 * 2. Get all items for this product in the household
 * 3. If no items found by productId, fall back to searching all items for matching barcode
 * 4. Fetch instances for all matching items
 * 5. Filter for active instances and sort by FIFO (earliest expiry first)
 *
 * The fallback handles cases where:
 * - Product was created through a different flow with a different ID
 * - There are duplicate products with the same barcode
 *
 * @param householdId - The household to search in
 * @param barcode - The barcode to look up (empty string disables the query)
 * @returns Query result with product, items, and sorted active instances
 */
export function useFindInventoryItemByBarcode(householdId: string, barcode: string) {
  return useQuery({
    queryKey: [...queryKeys.items.byHousehold(householdId), 'byBarcode', barcode] as const,
    queryFn: async (): Promise<InventoryBarcodeResult> => {
      // Step 1: Look up product by barcode
      const product = await getProductByBarcode(barcode);

      // Step 2: Try to get items by productId first (most efficient)
      let items: ItemWithInstanceCounts[] = [];
      if (product) {
        items = await getItems(householdId, { productId: product.id });
      }

      // Step 3: Fallback - if no items found by productId, search all items for matching barcode
      // This handles cases where productId doesn't match due to duplicate products
      // Note: Barcode comparison normalizes to handle leading zeros (EAN-13 padding issues)
      if (items.length === 0) {
        const allItems = await getItems(householdId);
        const normalizedBarcode = normalizeBarcode(barcode);
        items = allItems.filter(
          (item) =>
            item.product?.barcode && normalizeBarcode(item.product.barcode) === normalizedBarcode
        );
      }

      // If still no items, return the product (if found) for creating a new pending item
      if (items.length === 0) {
        return { product, instances: [], items: [] };
      }

      // Use the first item's product if we didn't find one via direct lookup
      const resolvedProduct = product ?? items[0]?.product ?? null;

      // Step 4: Fetch instances for all items
      const instancePromises = items.map((item) => getItemInstances(householdId, item.id));
      const instanceArrays = await Promise.all(instancePromises);

      // Step 5: Flatten and filter for active instances only
      const allInstances = instanceArrays.flat();
      const activeInstances = allInstances.filter((instance) => instance.status === 'active');

      // Step 6: Sort by expiry date (FIFO - earliest first, nulls last)
      const sortedInstances = activeInstances.sort((a, b) => {
        if (a.expiryDate === null && b.expiryDate === null) return 0;
        if (a.expiryDate === null) return 1;
        if (b.expiryDate === null) return -1;
        return a.expiryDate.localeCompare(b.expiryDate);
      });

      return { product: resolvedProduct, instances: sortedInstances, items };
    },
    enabled: !!householdId && !!barcode && barcode.length > 0,
    staleTime: 0, // Don't cache - always fetch fresh to avoid stale inventory state
    gcTime: 30 * 1000, // Keep in cache for 30 seconds for deduplication
  });
}

// ============================================================================
// Auto-Group Sweep
// ============================================================================

/**
 * Trigger a background auto-group sweep for all ungrouped items in a household.
 */
async function autoGroupSweep(householdId: string): Promise<AutoGroupSweepResult> {
  return apiFetch<AutoGroupSweepResult>(`/households/${householdId}/items/auto-group-sweep`, {
    method: 'POST',
  });
}

/**
 * Hook to trigger the auto-group sweep on mount.
 * Invalidates item and product group queries when items are grouped.
 */
export function useAutoGroupSweep() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ householdId }: { householdId: string }) => autoGroupSweep(householdId),
    onSuccess: (result, { householdId }) => {
      if (result.grouped.length > 0) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.items.byHousehold(householdId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.productGroups.byHousehold(householdId),
        });
      }
    },
  });
}

// ============================================================================
// Stock Target
// ============================================================================

async function setItemStockTarget(
  householdId: string,
  itemId: string,
  data: SetItemStockTargetDto
): Promise<Item> {
  return apiFetch<Item>(`/households/${householdId}/items/${itemId}/stock-target`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/**
 * Hook to set the stock target for an individual item.
 * Invalidates the household's items cache on success.
 */
export function useSetItemStockTarget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      householdId,
      itemId,
      data,
    }: {
      householdId: string;
      itemId: string;
      data: SetItemStockTargetDto;
    }) => setItemStockTarget(householdId, itemId, data),
    onSuccess: (_, { householdId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.items.byHousehold(householdId),
      });
    },
  });
}
